import Phaser from 'phaser';
import { getGameplayColorHex } from '../config/colors';
import { PROJECTILE_SPEED } from '../config/constants';
import { reducedParticles, fxDisabled } from '../config/particleConfig';

export class ProjectileSprite {
  private img: Phaser.GameObjects.Image;
  private trail: Phaser.GameObjects.Particles.ParticleEmitter | null;

  constructor(
    scene: Phaser.Scene,
    startX: number,
    startY: number,
    targetX: number,
    targetY: number,
    colorIndex: number,
    onHit: () => void,
  ) {
    const tint = getGameplayColorHex(colorIndex);

    // Glowing projectile image
    this.img = scene.add.image(startX, startY, `projectile-${colorIndex}`);
    this.img.setDisplaySize(20, 20);

    // Instant mode: skip all visuals, fire onHit immediately
    if (fxDisabled) {
      this.trail = null;
      onHit();
      this.img.destroy();
      return;
    }

    // Particle trail following the projectile (skipped during win speed-up)
    if (!reducedParticles) {
      this.trail = scene.add.particles(0, 0, 'particle', {
        follow: this.img,
        speed: { min: 5, max: 20 },
        scale: { start: 0.6, end: 0 },
        alpha: { start: 0.7, end: 0 },
        lifespan: 180,
        frequency: 15,
        tint,
        blendMode: Phaser.BlendModes.ADD,
      });
    } else {
      this.trail = null;
    }

    const distance = Math.hypot(targetX - startX, targetY - startY);
    const duration = (distance / PROJECTILE_SPEED) * 1000;

    scene.tweens.add({
      targets: this.img,
      x: targetX,
      y: targetY,
      duration: Math.max(duration, 50),
      ease: 'Linear',
      onComplete: () => {
        onHit();

        // Impact spark burst (skipped during win speed-up)
        if (!reducedParticles) {
          const spark = scene.add.particles(targetX, targetY, 'particle', {
            speed: { min: 40, max: 120 },
            scale: { start: 0.8, end: 0 },
            alpha: { start: 1, end: 0 },
            lifespan: 250,
            tint,
            emitting: false,
          });
          spark.explode(8);
          scene.time.delayedCall(300, () => spark.destroy());
        }

        if (this.trail) {
          this.trail.stop();
          const trailRef = this.trail;
          scene.time.delayedCall(200, () => trailRef.destroy());
        }
        this.img.destroy();
      },
    });
  }
}
